import React, {FunctionComponent, useCallback, useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {ConnectedRouter} from 'connected-react-router';
import {FocusStyleManager} from '@blueprintjs/core';
import {Helmet} from 'react-helmet';

import SecurityProvider from './shared/routing/SecurityProvider';
import RouterSwitch from './shared/routing/RouterSwitch';
import {history} from './shared/store';
import Header from './shared/components/layout/Header/Header';
import config from './config';
import {loadWorkspaces} from './shared/store/actions/workspaceActions';
import {loadCurrentUser, loadRecipients} from './shared/store/actions/userActions';
import {IState} from './shared/store/reducers';
import {mapPathToPageName} from './shared/utils/segment';
import {isLoggedIn} from './shared/utils/auth';
import {getFaviconPath} from './shared/utils/favicon';
import {WorkspaceConfigContextProvider} from './shared/hooks/use-workspace-config';
import SubscriptionManagerContainer from './shared/components/subscription/SubscriptionManagerContainer';
import {MainSearchContainer} from './shared/components/MainSearch';
import {GlobalLeftMenu} from './shared/components/left-menu/GlobalLeftMenu';
import {PanelWithLeftMenu} from './shared/components/layout/PanelWithLeftMenu';
import {WorkspaceSelectionContainer} from './shared/components/workspaces/WorkspaceSelectionContainer';
import {UserWorkspaceProvider, useCurrentUser, useCurrentWorkspace} from "./shared/hooks";
import {loadFolders} from './shared/store/actions/folderActions';
import {loadFavorites} from './shared/store/actions/favoriteActions';
import {loadTags} from './shared/store/actions/tagActions';

import './App.scss';

FocusStyleManager.onlyShowFocusOnTabs();

declare global {
  interface Window {
    analytics: any;
    tinymce: any;
  }
}

type AppComponentProps = {};

function AppContent({}) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showWorkspaceSelection, setShowWorkspaceSelection] = useState(false);
  const isDarkMode = useSelector((state: IState) => state.ui.isDarkMode);
  const workspaces = useSelector((state: IState) => state.workspaces?.workspaces);
  const currentUser = useCurrentUser();
  const currentWorkspace = useCurrentWorkspace();
  const customerName = config.app.customer_name;
  const faviconPath = useMemo(() => getFaviconPath(), []);

  useEffect(() => {
    /*
     * it feels a bit hacky, but we have to apply `bp4-dark` class to the entire body element,
     * in order to make Portals work as well;
     */
    if (isDarkMode) {
      window.document.body.classList.add('bp4-dark');
    } else {
      window.document.body.classList.remove('bp4-dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    dispatch(loadCurrentUser()); // used in Header area
  }, []);

  useEffect(() => {
    if (isLoggedIn(currentUser)) {
      const queryParams = new URLSearchParams(location.search);
      dispatch(loadWorkspaces({})); // used in Header area
      dispatch(loadRecipients()); // used in Select Recipients inputs
      /*
       * Used by Reports
       */
      dispatch(loadFolders());
      dispatch(loadFavorites());
      dispatch(loadTags());
    }
  }, [currentUser]);

  // Check if there is only one workspace
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (workspaces && queryParams.has('login')) {
      if (workspaces.length === 1) {
        history.replace({pathname: location.pathname, search: ''});
      }
      if (workspaces.length > 1) {
        setShowWorkspaceSelection(true);
      }
    }
  }, [workspaces, location.search]);

  const trackPageView = useCallback(
    (newState: any) => {
      if (isLoggedIn(currentUser)) {
        if (config.segment.enabled) {
          const title = newState.title;
          const uid = currentUser?.uid;
          const currentOrganizationUid =
            currentUser?.current_organization_uid || '';
          // track page call when title has changed in the component
          if (title && window.analytics) {
            const options = {
              context: {
                groupId: currentOrganizationUid,
              },
            };

            const currentPath = window.location.pathname;
            const pageName = mapPathToPageName(currentPath, document.title);
            window.analytics.page(pageName, {userId: uid}, options);
          }
        }
      }
    },
    [currentUser]
  );

  if (showWorkspaceSelection) {
    return (
      <div className={`root`}>
        <WorkspaceSelectionContainer/>
      </div>
    );
  }

  return (
    <div className={`root`}>
      <Helmet
        onChangeClientState={trackPageView}
        titleTemplate={`%s | ${customerName}`}
      >
        <link
          id="favicon"
          rel="icon"
          type="image/x-icon"
          href={faviconPath}
          sizes="32x32"
        />
      </Helmet>
      <WorkspaceConfigContextProvider workspace={currentWorkspace}>
        <Header/>
        <main className="layout__body">
          <PanelWithLeftMenu menuContent={<GlobalLeftMenu/>} large={true}>
            <RouterSwitch/>
          </PanelWithLeftMenu>
        </main>
      </WorkspaceConfigContextProvider>

      <MainSearchContainer/>

      <SubscriptionManagerContainer/>
    </div>
  );
}

const AppComponent: FunctionComponent<
  AppComponentProps
> = ({}: AppComponentProps) => {
  return (
    <ConnectedRouter history={history}>
      <SecurityProvider>
        <UserWorkspaceProvider>
          <AppContent/>
        </UserWorkspaceProvider>
      </SecurityProvider>
    </ConnectedRouter>
  );
};

export default AppComponent;
